import { useRef, useState } from "react";
import { ArrowRight, Check, FileSearch, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import UploadArea from "@/components/UploadArea";
import { captureLead, processTicketPhoto, trackFunnelEvent, type OcrProcessResult, type PremiumProductKey } from "@/lib/api";
import { prepareOcrUploadFile } from "@/lib/image-upload";
import { getAcquisitionSessionId, persistHeroOcr } from "@/lib/hero-funnel";
import { PRODUCT_VALUES, track } from "@/lib/analytics";

const productPrice = (productKey: PremiumProductKey) => `$${PRODUCT_VALUES[productKey]}`;

const secondaryProducts: Array<{ key: PremiumProductKey; title: string; body: string; cta: string }> = [
  { key: "court_pdf", title: "Court PDF", body: "Need the court-oriented PDF only?", cta: "Prepare Court PDF" },
  { key: "records_request", title: "Records Pack", body: "Need supporting-record request materials?", cta: "Build Records Pack" },
  { key: "mail_filing", title: "Mail Filing", body: "Need the filing and mailing workflow?", cta: "Prepare Mail Filing" },
];

const checks = [
  ["Citation Intelligence", "We pull officer-entered facts directly from the citation."],
  ["California Code Check", "We connect the alleged violation to California-specific rules and context."],
  ["Radar / LIDAR Context", "When relevant, we identify calibration, training, and supporting-record questions that may matter."],
  ["Location Evidence", "We examine the reported location and available roadway context."],
  ["Conditions Context", "We check relevant conditions and case context when the information is available."],
  ["Evidence Gaps", "We separate what is established from what still needs supporting records."],
  ["Court Options", "We identify available self-help court-response options when eligibility is supported."],
  ["Court Preparation", "We turn the review into organized filing-oriented materials when the user chooses to proceed."],
] as const;

const comparisons = [
  ["Starts with what you type.", "Starts with the actual citation."],
  ["Relies on your memory.", "Reads officer-entered facts first."],
  ["Puts you into a generic ticket category.", "Checks the alleged violation and citation-specific context."],
  ["Gives you a checklist.", "Separates what is supported from what still needs proof."],
  ["May ask you to commit before showing much case-specific value.", "Shows an initial citation review first."],
] as const;

const consequences = [
  {
    eyebrow: "FINE + FEES",
    bigLine: "BASE FINE \u2260 TOTAL",
    bodyBefore: "A base fine is rarely the full story. A California ticket can turn into ",
    highlight: "$490+ today",
    bodyAfter: " once fees and assessments stack on top.",
    small: null,
  },
  {
    eyebrow: "INSURANCE",
    bigLine: "THE TICKET CAN KEEP COSTING YOU",
    bodyBefore: "A moving violation can keep costing you after the court bill is gone. For many drivers, the real hit can be ",
    highlight: "+$1,400 to $2,100 over 3 years",
    bodyAfter: " in higher insurance.",
    small: "Actual impact depends on violation, driver, insurer, and history.",
  },
  {
    eyebrow: "DRIVING RECORD",
    bigLine: "THE PAYMENT IS FAST. THE RECORD ISN'T.",
    bodyBefore: "You can pay the ticket in minutes. The conviction can sit on your California driving record for ",
    highlight: "years",
    bodyAfter: ".",
    small: null,
  },
] as const;

type OcrStage = "idle" | "uploading" | "reading" | "extracting" | "checking" | "found";

const Landing = () => {
  const navigate = useNavigate();
  const uploadRef = useRef<HTMLDivElement>(null);
  const [ocr, setOcr] = useState<OcrProcessResult | null>(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<OcrStage>("idle");
  const [error, setError] = useState<string | null>(null);

  const focusUpload = () => {
    const target = uploadRef.current;
    if (!target) return;
    if (typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    target.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
  };

  const facts = ocr
    ? [ocr.violation_code, ocr.court_name, ocr.location_text].filter((value): value is string => Boolean(value?.trim()))
    : [];

  const onFile = async (file: File) => {
    setBusy(true);
    setError(null);
    setOcr(null);
    setStage("uploading");
    void trackFunnelEvent("hero_upload_started", {
      stage: "hero_upload",
      metadata: { file_type: file.type === "application/pdf" ? "pdf" : "image" },
    });

    try {
      setStage("reading");
      const result = await processTicketPhoto(await prepareOcrUploadFile(file));
      setStage("extracting");
      if (!result.ticket_id || result.extraction_status === "needs_manual_entry") {
        throw new Error(result.warning || "We could not read enough of the citation. Try a clearer photo or PDF.");
      }
      setStage("checking");
      persistHeroOcr(result);
      setOcr(result);
      setStage("found");
      void trackFunnelEvent("hero_upload_completed", {
        stage: "hero_upload",
        ticket_id: result.ticket_id,
        metadata: { extraction_status: result.extraction_status },
      });
    } catch (uploadError) {
      setStage("idle");
      setError(uploadError instanceof Error ? uploadError.message : "We could not read that upload. Try a clearer image or PDF.");
      void trackFunnelEvent("ocr_failed", { stage: "hero_upload", metadata: { source: "hero" } });
    } finally {
      setBusy(false);
    }
  };

  const continueReview = async () => {
    if (!ocr?.ticket_id || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Enter a valid email so we can save your free review.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await captureLead({ ticket_id: ocr.ticket_id, email: email.trim(), acquisition_session_id: getAcquisitionSessionId() });
      navigate(`/new?hero_ticket=${encodeURIComponent(ocr.ticket_id)}`);
    } catch (leadError) {
      setError(leadError instanceof Error ? leadError.message : "We could not save your review. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const selectProduct = (key: PremiumProductKey) => {
    track("product_selected", { product_key: key, source_page: "/" });
    void trackFunnelEvent("product_selected", { stage: "pricing", metadata: { product_key: key } });
    focusUpload();
  };

  const stageLabel = {
    uploading: "UPLOADING CITATION",
    reading: "READING CITATION",
    extracting: "EXTRACTING TICKET FACTS",
    checking: "CHECKING CALIFORNIA VIOLATION",
    found: "CITATION FOUND",
    idle: "",
  }[stage];

  return (
    <div className="min-h-screen overflow-x-clip bg-white text-slate-950 selection:bg-blue-200 selection:text-slate-950">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1160px] items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <button className="min-w-0 text-left text-lg font-black text-slate-950" onClick={() => navigate("/")} type="button">
            JusticeDrive <span className="ml-2 align-middle text-[11px] font-black uppercase tracking-normal text-blue-700">California only</span>
          </button>
          <button
            className="min-h-10 shrink-0 rounded-md border border-slate-950 bg-blue-600 px-3 text-xs font-black text-white shadow-[3px_3px_0_#0f172a] transition-transform hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:px-4 sm:text-sm"
            onClick={focusUpload}
            type="button"
          >
            <span className="sm:hidden">Check Free</span>
            <span className="hidden sm:inline">Check My Ticket Free</span>
            <span className="ml-2 hidden text-[11px] font-bold text-blue-100 md:inline">No credit card</span>
          </button>
        </div>
      </header>

      <main>
        <section className="border-b border-slate-200 bg-slate-50 px-4 py-10 sm:px-6 sm:py-14 lg:py-16">
          <div className="mx-auto grid max-w-[1160px] items-center gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
            <div className="min-w-0">
              <p className="mb-4 text-xs font-black uppercase tracking-normal text-blue-700">CALIFORNIA TRAFFIC TICKET REVIEW</p>
              <h1
                aria-label="Dont Pay That California Ticket Yet."
                className="max-w-[640px] text-[clamp(2.35rem,10vw,3.25rem)] font-black leading-[1.02] tracking-normal text-slate-950 sm:text-[clamp(2.6rem,5vw,3.25rem)]"
              >
                <span>Dont Pay That </span>
                <span className="block sm:inline">California </span>
                <span>Ticket Yet.</span>
              </h1>
              <p className="mt-5 max-w-[560px] text-2xl font-black leading-tight text-slate-950 sm:text-3xl">
                The fine is only the first bill.
              </p>
              <div className="mt-5 max-w-[650px] space-y-4 text-base font-semibold leading-7 text-slate-800">
                <p>
                  Before you{" "}
                  <span className="rounded-sm bg-[#ffd400] px-1 font-black text-slate-950">plead guilty with a payment</span>,
                  check what the officer actually wrote.
                </p>
                <p>
                  Upload your California traffic ticket. JusticeDrive reads the citation, checks the alleged Vehicle Code violation,
                  citation facts, location, radar or LIDAR context where relevant, evidence gaps, and available court-response options.
                </p>
              </div>
              <p className="mt-5 max-w-[560px] text-base font-black leading-6 text-slate-950">
                See what may actually be worth checking before you decide what to do.
              </p>
            </div>

            <div
              ref={uploadRef}
              className="min-w-0 max-w-full overflow-hidden rounded-lg border-2 border-slate-950 bg-white p-4 shadow-[4px_4px_0_#2563eb] sm:p-5 sm:shadow-[8px_8px_0_#2563eb]"
            >
              {!ocr ? (
                <>
                  <div className="flex items-center gap-3 border-b border-slate-200 pb-4">
                    <FileSearch className="h-7 w-7 shrink-0 text-blue-600" />
                    <div>
                      <h2 className="text-2xl font-black leading-tight text-slate-950">Drop your ticket here</h2>
                      <p className="text-sm font-bold text-slate-600">Photo, screenshot or PDF</p>
                    </div>
                  </div>
                  <UploadArea
                    allowPdf
                    actionStyle="prominent"
                    cameraLabel="Take a Photo of My Ticket"
                    className="mt-4 flex min-h-[280px] w-full min-w-0 max-w-full items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50"
                    helperText="Take a photo, choose an existing image, or upload a PDF."
                    onFileSelect={(file) => void onFile(file)}
                    uploadLabel="Choose Existing Photo or PDF"
                  />
                  <div className="mt-4 grid min-w-0 grid-cols-2 gap-2 text-center text-xs font-black uppercase tracking-normal text-slate-700 sm:grid-cols-4">
                    {["Free citation review", "No credit card", "About 2 minutes", "California only"].map((item) => (
                      <span className="min-w-0 rounded-md border border-slate-200 bg-slate-50 px-2 py-2 leading-tight [overflow-wrap:anywhere]" key={item}>
                        {item}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <div>
                  <p className="text-xs font-black uppercase tracking-normal text-blue-700">CITATION FOUND</p>
                  <h2 className="mt-2 text-3xl font-black leading-tight text-slate-950">We Found Your Citation.</h2>
                  <div className="mt-5 space-y-2 rounded-md border border-slate-200 bg-blue-50 p-4">
                    {facts.map((fact) => <p key={fact} className="font-black text-slate-950">{fact}</p>)}
                  </div>
                  <label className="mt-5 block text-sm font-black uppercase tracking-normal text-slate-950">
                    Where should we save your free review?
                    <input
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="mt-2 min-h-12 w-full rounded-md border border-slate-300 bg-white px-4 text-base font-normal normal-case outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200"
                      type="email"
                      placeholder="you@example.com"
                    />
                  </label>
                  <button
                    disabled={busy}
                    onClick={() => void continueReview()}
                    className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-md border border-slate-950 bg-blue-600 px-4 font-black text-white shadow-[4px_4px_0_#0f172a] transition-transform hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none disabled:opacity-50"
                    type="button"
                  >
                    Continue Free Review <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              )}
              {busy ? <p className="mt-4 rounded-md bg-slate-950 px-3 py-2 text-center text-xs font-black uppercase tracking-normal text-white">{stageLabel || "Working"}...</p> : null}
              {error ? <p className="mt-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm font-black text-red-800" role="alert">{error}</p> : null}
            </div>
          </div>
          <p className="mx-auto mt-7 max-w-[1160px] text-sm font-semibold leading-6 text-slate-700">
            Not a law firm. Self-help information and document-preparation tools.{" "}
            <span className="font-black text-slate-950">Before you pay the ticket, check the ticket.</span>
          </p>
        </section>

        <section className="border-b border-slate-200 bg-white px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-[1160px]">
            <h2 className="max-w-[760px] text-3xl font-black leading-tight text-slate-950 sm:text-[32px]">
              Paying Ends the Notice. Not the Consequences.
            </h2>
            <p className="mt-4 max-w-[620px] text-base font-semibold leading-7 text-slate-700">
              A traffic ticket can cost more than the number you see today.
            </p>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {consequences.map((item) => (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-5" key={item.eyebrow}>
                  <p className="text-xs font-black uppercase tracking-normal text-blue-700">{item.eyebrow}</p>
                  <p className="mt-4 text-2xl font-black leading-tight text-slate-950">{item.bigLine}</p>
                  <p className="mt-4 text-sm font-semibold leading-6 text-slate-700">
                    {item.bodyBefore}
                    <span className="rounded-sm bg-[#ffd400] px-1 font-black text-slate-950">{item.highlight}</span>
                    {item.bodyAfter}
                  </p>
                  {item.small ? <p className="mt-4 text-xs font-semibold leading-5 text-slate-600">{item.small}</p> : null}
                </div>
              ))}
            </div>
            <p className="mt-8 max-w-[620px] text-2xl font-black leading-tight text-slate-950">
              Two minutes now beats paying first and asking questions later.
            </p>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-slate-50 px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-[1160px]">
            <h2 className="max-w-[900px] text-3xl font-black leading-tight text-slate-950 sm:text-[32px]">
              Most Ticket Sites Ask What Happened. JusticeDrive Starts With What the Officer Wrote.
            </h2>
            <div className="mt-8 overflow-hidden rounded-lg border border-slate-300 bg-white">
              <div className="grid grid-cols-2 border-b border-slate-300 text-xs font-black uppercase tracking-normal">
                <div className="border-r border-slate-300 bg-slate-100 p-4 text-slate-500">TYPICAL TICKET SITE</div>
                <div className="bg-blue-600 p-4 text-white">JUSTICEDRIVE</div>
              </div>
              {comparisons.map(([typical, justice]) => (
                <div className="grid grid-cols-2 border-b border-slate-200 last:border-b-0" key={typical}>
                  <div className="border-r border-slate-200 p-4 text-sm font-semibold leading-6 text-slate-500 sm:text-base">{typical}</div>
                  <div className="p-4 text-sm font-black leading-6 text-slate-950 sm:text-base">{justice}</div>
                </div>
              ))}
            </div>
            <p className="mt-8 max-w-[700px] text-2xl font-black leading-tight text-slate-950">
              Your ticket is evidence. We treat it like evidence.
            </p>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-slate-950 px-4 py-14 text-white sm:px-6 sm:py-16">
          <div className="mx-auto max-w-[1160px]">
            <h2 className="max-w-[760px] text-3xl font-black leading-tight sm:text-[32px]">
              A Ticket Is More Than a Code. Its a Stack of Assumptions.
            </h2>
            <p className="mt-4 max-w-[720px] text-base font-semibold leading-7 text-slate-300">
              Every code, box, location, device, fact, and missing record can change what is worth checking.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {checks.map(([title, copy], index) => (
                <div className="rounded-lg border border-white/15 bg-white/[0.04] p-5" key={title}>
                  <p className="text-xs font-black uppercase tracking-normal text-blue-300">{String(index + 1).padStart(2, "0")}</p>
                  <ShieldCheck className="mt-5 h-5 w-5 text-blue-300" />
                  <h3 className="mt-4 text-xl font-black leading-tight text-white">{title}</h3>
                  <p className="mt-3 text-sm font-semibold leading-6 text-slate-300">{copy}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 max-w-[620px] text-2xl font-black leading-tight text-white">
              This isnt a traffic-ticket quiz. Its a citation review engine.
            </p>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-[1160px]">
            <p className="text-xs font-black uppercase tracking-normal text-blue-700">NO MAGIC NUMBERS</p>
            <h2 className="mt-3 max-w-[780px] text-3xl font-black leading-tight text-slate-950 sm:text-[32px]">
              No "97% Success." No Magic Dismissal Rate. No Courtroom Fairy Tales.
            </h2>
            <div className="mt-7 max-w-[620px] space-y-3 text-lg font-black leading-7 text-slate-950">
              <p>If the citation supports something, we show it.</p>
              <p>If something looks questionable, we show it.</p>
              <p>If the evidence isnt there yet, we say that too.</p>
            </div>
            <p className="mt-7 text-2xl font-black leading-tight text-blue-700">Bad news included. Thats the point.</p>
            <p className="mt-4 max-w-[680px] text-base font-semibold leading-7 text-slate-700">
              We dont promise your ticket disappears. We show you what may actually be worth checking.
            </p>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-slate-50 px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto grid max-w-[1160px] gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <h2 className="text-3xl font-black leading-tight text-slate-950 sm:text-[32px]">
                See What We Find Before We Ask You to Pay.
              </h2>
              <p className="mt-4 text-base font-black leading-7 text-slate-700">Your first citation review is free.</p>
            </div>
            <div className="rounded-lg border border-slate-300 bg-white p-5 shadow-[6px_6px_0_#2563eb] sm:p-6">
              <p className="inline-block rounded-md bg-slate-950 px-2 py-1 text-xs font-black uppercase tracking-normal text-white">
                ILLUSTRATIVE EXAMPLE
              </p>
              <h3 className="mt-5 text-2xl font-black leading-tight text-slate-950">California Speeding CVC 22350</h3>
              <p className="mt-6 text-xl font-black text-slate-950">3 things worth checking</p>
              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                {["Citation details extracted", "Alleged violation identified", "Location and available context reviewed"].map((item) => (
                  <p className="rounded-md border border-slate-200 bg-blue-50 p-3 text-sm font-black leading-5 text-slate-950" key={item}>
                    <Check className="mr-2 inline h-4 w-4 text-blue-700" />
                    {item}
                  </p>
                ))}
              </div>
              <div className="mt-5 space-y-2">
                {[
                  "Supporting radar or LIDAR records are not currently available.",
                  "Additional roadway or survey evidence may require verification.",
                  "Officer or device records may matter depending on the defense theory.",
                ].map((item) => (
                  <p className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm font-semibold leading-6 text-slate-700" key={item}>{item}</p>
                ))}
              </div>
              <p className="mt-6 border-t border-slate-200 pt-5 text-lg font-black leading-7 text-slate-950">
                Facts are facts. Missing evidence is missing evidence. We keep them separate.
              </p>
              <p className="mt-3 text-xs font-semibold leading-5 text-slate-600">
                Your actual review depends on your citation and the information available.
              </p>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-[1160px]">
            <h2 className="text-3xl font-black leading-tight text-slate-950 sm:text-[32px]">How It Works</h2>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {[
                ["01", "DROP YOUR TICKET", "Take a photo or upload the citation."],
                ["02", "SEE WHAT HOLDS UP", "We read the citation and show what looks supported, questionable, or incomplete."],
                ["03", "DECIDE WHAT TO DO", "Pay it, prepare, or fight it based on what your actual citation shows."],
              ].map(([number, title, copy]) => (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-5" key={number}>
                  <p className="text-xs font-black uppercase tracking-normal text-blue-700">STEP {number}</p>
                  <h3 className="mt-4 text-xl font-black leading-tight text-slate-950">{title}</h3>
                  <p className="mt-3 text-sm font-semibold leading-6 text-slate-700">{copy}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 max-w-[560px] text-2xl font-black leading-tight text-slate-950">
              Maybe pay it. Maybe fight it. First, know what youre looking at.
            </p>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-slate-50 px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-[1160px]">
            <h2 className="text-3xl font-black leading-tight text-slate-950 sm:text-[32px]">
              Know Before You Pay. Prepare If You Fight.
            </h2>
            <p className="mt-4 text-base font-black leading-7 text-slate-700">
              Free to check. Preparation from {PRODUCT_VALUES.court_pdf}. Full Defense {PRODUCT_VALUES.premium_defense}.
            </p>
            <div className="mt-8 grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
              <div className="rounded-lg border-2 border-slate-950 bg-white p-6 shadow-[8px_8px_0_#2563eb] sm:p-7">
                <p className="inline-block rounded-md bg-blue-600 px-2 py-1 text-xs font-black uppercase tracking-normal text-white">MOST COMPLETE</p>
                <h3 className="mt-5 text-2xl font-black leading-tight text-slate-950">California Full Defense</h3>
                <p className="mt-2 text-4xl font-black leading-none text-slate-950">
                  {productPrice("premium_defense")} <span className="text-sm font-bold text-slate-600">one-time</span>
                </p>
                <p className="mt-5 text-base font-semibold leading-7 text-slate-700">
                  Turn your citation review into an organized defense workflow.
                </p>
                <ul className="mt-5 grid gap-2 text-sm font-semibold leading-6 text-slate-800 sm:grid-cols-2">
                  {[
                    "Full citation analysis",
                    "Defense angles prioritized",
                    "Evidence records checklist",
                    "TR-205 preparation where applicable",
                    "Court-ready documents where supported",
                    "Filing guidance",
                  ].map((item) => (
                    <li key={item}><Check className="mr-2 inline h-4 w-4 text-blue-700" />{item}</li>
                  ))}
                </ul>
                <button
                  className="mt-7 min-h-12 w-full rounded-md border border-slate-950 bg-blue-600 px-4 font-black text-white shadow-[4px_4px_0_#0f172a] transition-transform hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none"
                  onClick={() => selectProduct("premium_defense")}
                  type="button"
                >
                  Prepare My Full Defense {productPrice("premium_defense")}
                </button>
                <p className="mt-4 text-center text-xs font-bold uppercase tracking-normal text-slate-600">
                  One payment. No subscription. Built from your citation.
                </p>
              </div>
              <div className="grid gap-4">
                {secondaryProducts.map((product) => (
                  <div className="rounded-lg border border-slate-200 bg-white p-5" key={product.key}>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="text-xl font-black leading-tight text-slate-950">
                          {product.title} <span className="text-blue-700">{productPrice(product.key)}</span>
                        </h3>
                        <p className="mt-2 text-sm font-semibold leading-6 text-slate-700">{product.body}</p>
                      </div>
                      <button
                        className="min-h-11 shrink-0 rounded-md border border-slate-950 bg-white px-3 py-2 text-xs font-black text-slate-950 transition-colors hover:bg-blue-50"
                        onClick={() => selectProduct(product.key)}
                        type="button"
                      >
                        {product.cta}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-blue-600 px-4 py-14 text-center text-white sm:px-6 sm:py-16">
          <div className="mx-auto max-w-[760px]">
            <h2 className="text-3xl font-black leading-tight sm:text-[32px]">
              Before You Pay It,
              <br />
              Check What They Wrote.
            </h2>
            <p className="mx-auto mt-4 max-w-[620px] text-base font-semibold leading-7 text-blue-50">
              Upload your California traffic citation and see what may actually be worth reviewing.
            </p>
            <button
              onClick={focusUpload}
              className="mt-7 min-h-12 rounded-md border border-white bg-white px-6 py-3 text-base font-black text-blue-700 shadow-[4px_4px_0_#0f172a] transition-transform hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none"
              type="button"
            >
              Check My Ticket Free
            </button>
            <p className="mt-4 text-xs font-black uppercase tracking-normal text-blue-50">No credit card  About 2 minutes  California only</p>
            <p className="mt-5 text-sm font-semibold leading-6 text-blue-50">
              Pay it if it makes sense. Fight it if it makes sense. Just dont decide blind.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Landing;
